// Rename an org key's route-pin label from the Advanced drawer (#5211, epic
// #5200 slice 03) — the dashboard's client for `PATCH /v1/billing/keys/:id/label`
// (`apps/api/src/routes/key-label.ts`), which does not otherwise have one. The
// server refuses with its OWN reason (`label_taken` / `label_in_use` /
// `label_pinned`), never a generic 409 — read the extensions' `reason`, never
// guess from the status alone, the same discipline `route-refusal.ts` and
// `key-label.ts`'s own `refusal()` switch apply.

import { ApiError, apiFetch } from './api';
import type { KeyRow } from './keys-types';
import { SESSION_TOKEN } from './session';

/** Rename (or, with `label: null`, clear) a key's pin label. Throws on a refusal. */
export function setKeyLabel(
  keyId: string,
  label: string | null,
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/billing/keys/${encodeURIComponent(keyId)}/label`, token, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

export type KeyLabelRefusal =
  /** Another key in this account already holds the new label. */
  | { readonly kind: 'label_taken' }
  /** Pending work still names the OLD label — the server's own count in `detail`. */
  | { readonly kind: 'label_in_use'; readonly detail: string }
  /** A CLEAR while a route pin still names the label. */
  | { readonly kind: 'label_pinned'; readonly routes: number }
  /** Any other refusal — the server's `detail`, no cure offered. */
  | { readonly kind: 'refused'; readonly detail: string | null };

/** The one reading of a refused rename — `key-label.ts`'s three named 409s, by `reason`. */
export function keyLabelRefusalOf(error: unknown): KeyLabelRefusal {
  if (!(error instanceof ApiError)) return { kind: 'refused', detail: null };
  const reason = error.extensions?.['reason'];
  if (reason === 'label_taken') return { kind: 'label_taken' };
  if (reason === 'label_in_use') return { kind: 'label_in_use', detail: error.detail ?? '' };
  if (reason === 'label_pinned') {
    const routes = error.extensions?.['routes'];
    return { kind: 'label_pinned', routes: typeof routes === 'number' ? routes : 0 };
  }
  return { kind: 'refused', detail: error.detail ?? null };
}
